/**
 * Steuert die Zoomstufe der Weltkarte.
 *
 * Die Stufe folgt dem Spielzustand, nicht der Mausrad-Eingabe: Naehert sich das
 * Schiff einem Ort von Interesse, zieht die Kamera von selbst herunter.
 *
 * Entscheidend sind die getrennten Ein- und Ausstiegsschwellen. Mit einer
 * einzelnen Schwelle wuerde die Kamera genau an der Grenze bei jedem Frame
 * zwischen zwei Stufen springen — ein Fehler, den man im Code nicht sieht,
 * sondern erst im fertigen Spiel als Zittern bemerkt.
 */

export type ZoomLevel = 'strategic' | 'transit' | 'tactical';

export interface ZoomThresholds {
  /** Ab dieser Naehe zu einem Ort wird auf die taktische Stufe gewechselt. */
  readonly tacticalEnterNm: number;
  /** Erst ab dieser Entfernung wird sie wieder verlassen. */
  readonly tacticalExitNm: number;
  readonly transitEnterNm: number;
  readonly transitExitNm: number;
}

export const DEFAULT_ZOOM_THRESHOLDS: ZoomThresholds = {
  tacticalEnterNm: 6,
  tacticalExitNm: 12,
  transitEnterNm: 90,
  transitExitNm: 140,
};

/** Sichtbare Kartenbreite je Stufe, in Seemeilen. */
export const VIEWPORT_WIDTH_NM: Readonly<Record<ZoomLevel, number>> = {
  // Die Karte misst rund 1141 x 1137 sm; etwas Rand darum herum.
  strategic: 1250,
  transit: 180,
  /**
   * Nicht enger als das Hoehenraster traegt: Es loest eine Bogenminute auf,
   * also rund eine Seemeile. Bei 12 sm Sichtfeld waere jeder Datenpunkt ueber
   * 50 Bildpunkte gross und die eingebackenen Tiefenlinien wuerden zu
   * Treppenmustern. 24 sm bleiben Nahbereich, ohne das zu erzwingen.
   */
  tactical: 24,
};

function assertOrdered(thresholds: ZoomThresholds): void {
  const { tacticalEnterNm, tacticalExitNm, transitEnterNm, transitExitNm } = thresholds;
  if (tacticalExitNm <= tacticalEnterNm || transitExitNm <= transitEnterNm) {
    throw new Error('ZoomDirector: Ausstiegsschwelle muss über der Einstiegsschwelle liegen');
  }
  if (transitEnterNm <= tacticalExitNm) {
    throw new Error('ZoomDirector: Transit-Einstieg muss über dem taktischen Ausstieg liegen');
  }
}

export class ZoomDirector {
  private readonly thresholds: ZoomThresholds;
  private level: ZoomLevel;

  constructor(thresholds: ZoomThresholds = DEFAULT_ZOOM_THRESHOLDS, initial: ZoomLevel = 'strategic') {
    assertOrdered(thresholds);
    this.thresholds = thresholds;
    this.level = initial;
  }

  get current(): ZoomLevel {
    return this.level;
  }

  /** Sichtbare Kartenbreite der aktuellen Stufe, in Seemeilen. */
  get viewportWidthNm(): number {
    return VIEWPORT_WIDTH_NM[this.level];
  }

  /**
   * Aktualisiert die Stufe anhand der Entfernung zum naechsten Ort von Interesse
   * und meldet die nun gueltige Stufe.
   */
  update(distanceToFocusNm: number): ZoomLevel {
    const { tacticalEnterNm, tacticalExitNm, transitEnterNm, transitExitNm } = this.thresholds;
    const distance = Number.isFinite(distanceToFocusNm)
      ? Math.max(0, distanceToFocusNm)
      : Number.POSITIVE_INFINITY;

    switch (this.level) {
      case 'tactical':
        // Nur verlassen, wenn deutlich entfernt — sonst zittert die Kamera.
        if (distance > tacticalExitNm) {
          this.level = distance > transitExitNm ? 'strategic' : 'transit';
        }
        break;

      case 'transit':
        if (distance <= tacticalEnterNm) this.level = 'tactical';
        else if (distance > transitExitNm) this.level = 'strategic';
        break;

      case 'strategic':
        if (distance <= tacticalEnterNm) this.level = 'tactical';
        else if (distance <= transitEnterNm) this.level = 'transit';
        break;
    }

    return this.level;
  }

  /** Setzt die Stufe hart, etwa beim Szenenwechsel. */
  reset(level: ZoomLevel): void {
    this.level = level;
  }
}

/**
 * Kamera-Zoomfaktor fuer eine gewuenschte Sichtweite.
 *
 * Massgeblich ist die *kleinere* Bildschirmkante. Wuerde nur die Breite zaehlen,
 * zeigte ein breites Fenster in der Uebersicht zwar die volle Kartenbreite, aber
 * nur einen Ausschnitt der Hoehe — die Karte waere nie vollstaendig zu sehen.
 * Weltkoordinaten sind Seemeilen, der Faktor ist daher schlicht das Verhaeltnis.
 */
export function zoomFactorFor(
  viewportNm: number,
  screenWidthPx: number,
  screenHeightPx: number,
): number {
  if (viewportNm <= 0) return 1;
  const shorterEdge = Math.min(screenWidthPx, screenHeightPx);
  return shorterEdge / viewportNm;
}
